import React, { useEffect, useState } from "react";
import { customFetch } from '../apiClient';

const UserRolesTable = () => {
  const hospitalId = sessionStorage.getItem("hospitalId") || "";
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editRoleId, setEditRoleId] = useState(null);
  const [editRoleData, setEditRoleData] = useState({});
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const fetchRoles = async () => {
    if (!hospitalId) {
      setError("Hospital ID not found in session. Access denied.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
    const res = await customFetch(`/hospitals/${hospitalId}/roles`);
      if (!res.ok) throw new Error("LoginId does not exist");
      const data = await res.json();
      setRoles(data);
    } catch (e) {
      setError(e.message);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, [hospitalId]);

  const startEditing = (role) => {
    setEditRoleId(role.rid);
    setEditRoleData({
      loginID: role.loginID,
      hid: role.hid,
      modules: role.modules,
      moduleName: role.moduleName,
      addRecord: role.addRecord || "N",
      editRecord: role.editRecord || "N",
      deleteRecord: role.deleteRecord || "N",
      delPass: role.delPass || "0",
      editPass: role.editPass || "0",
    });
    setError(null);
    setMessage(null);
  };

  const handleChange = (field, value) => {
    setEditRoleData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const cancelEditing = () => {
    setEditRoleId(null);
    setEditRoleData({});
    setError(null);
    setMessage(null);
  };

  const saveRole = async () => {
    if (!editRoleId) return;

    setLoading(true);
    setError(null);
    setMessage(null);

    try {
    const res = await customFetch(`/hospitals/${hospitalId}/roles/${editRoleId}`, {
  method: "PUT",
  headers: {
    "Content-Type": "application/json",
  },
  body: JSON.stringify(editRoleData),
});
      if (res.status === 403) setError("Permission denied to edit this role.");
      else if (!res.ok) {
        const text = await res.text();
        setError(text || "Failed to update role.");
      } else {
        setMessage("Role updated successfully.");
        setEditRoleId(null);
        setEditRoleData({});
        fetchRoles();
      }
    } catch (e) {
      setError("Network error: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteRole = async (rid) => {
    if (!window.confirm("Are you sure to delete this role?")) return;
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      const res = await customFetch(`/hospitals/${hospitalId}/roles/${rid}`, {
  method: "DELETE",
});
      if (res.status === 403) setError("Permission denied to delete this role.");
      else if (res.status === 404) setError("Role not found.");
      else if (!res.ok) {
        const text = await res.text();
        setError(text || "Failed to delete role.");
      } else {
        setMessage("Role deleted successfully.");
        if (editRoleId === rid) {
          setEditRoleId(null);
          setEditRoleData({});
        }
        fetchRoles();
      }
    } catch (e) {
      setError("Network error: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="user-roles-container">
      <div className="roles-card">
        {/* Header - Solid Theme Color */}
        <div className="roles-header">
          <div className="header-left">
            <div className="header-icon">
              <i className="bi bi-shield-lock-fill"></i>
            </div>
            <div className="header-text">
              <h5>User Roles Management</h5>
              <p>Manage access permissions and modules for users</p>
            </div>
          </div>
          <div className="header-badge">
            <span className="badge-count">{roles.length}</span>
            <span className="badge-label">Roles Found</span>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="alert-custom alert-danger slide-in">
            <i className="bi bi-exclamation-triangle-fill"></i>
            <span>{error}</span>
            <button className="alert-close" onClick={() => setError(null)}>
              <i className="bi bi-x"></i>
            </button>
          </div>
        )}
        {message && (
          <div className="alert-custom alert-success slide-in">
            <i className="bi bi-check-circle-fill"></i>
            <span>{message}</span>
            <button className="alert-close" onClick={() => setMessage(null)}>
              <i className="bi bi-x"></i>
            </button>
          </div>
        )}

        {/* Table Content */}
        <div className="table-wrapper">
          {loading && roles.length === 0 ? (
            <div className="loading-state">
              <div className="spinner-custom"></div>
              <p>Loading roles...</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="custom-table">
                <thead>
                  <tr>
                    <th>Login ID</th>
                    <th>Module Name</th>
                    <th className="text-center">Add</th>
                    <th className="text-center">Edit</th>
                    <th className="text-center">Delete</th>
                    <th className="text-center">DelPass</th>
                    <th className="text-center">EditPass</th>
                    <th className="text-center" style={{ width: "120px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {roles.length === 0 && !loading && (
                    <tr>
                      <td colSpan="8">
                        <div className="empty-state">
                          <i className="bi bi-inbox"></i>
                          <h6>No roles found</h6>
                          <p>No roles have been assigned yet.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                  {roles.map((role) => (
                    <tr key={role.rid} className={editRoleId === role.rid ? "editing-row" : ""}>
                      <td className="fw-semibold">{role.loginID}</td>
                      <td>
                        {editRoleId === role.rid ? (
                          <input
                            type="text"
                            className="form-control-custom readonly"
                            value={editRoleData.moduleName ?? ""}
                            readOnly
                          />
                        ) : (
                          <span className="module-badge">
                            <i className="bi bi-box"></i> {role.moduleName}
                          </span>
                        )}
                      </td>
                      
                      {/* Permission Dropdowns */}
                      {["addRecord", "editRecord", "deleteRecord"].map((field) => (
                        <td key={field} className="text-center">
                          {editRoleId === role.rid ? (
                            <select
                              className="form-select-custom"
                              value={editRoleData[field] || "N"}
                              onChange={(e) => handleChange(field, e.target.value)}
                            >
                              <option value="Y">Y</option>
                              <option value="N">N</option>
                            </select>
                          ) : (
                            <span className={`permission-badge ${role[field] === 'Y' ? 'granted' : 'denied'}`}>
                              {role[field] || "N"}
                            </span>
                          )}
                        </td>
                      ))}

                      {/* Passwords */}
                      {["delPass", "editPass"].map((field) => (
                        <td key={field} className="text-center">
                          {editRoleId === role.rid ? (
                            <input
                              type="text"
                              className="form-control-custom text-center"
                              value={editRoleData[field] ?? ""}
                              onChange={(e) => handleChange(field, e.target.value)}
                              placeholder="0"
                            />
                          ) : (
                            <span className="pass-text">{role[field] || "0"}</span>
                          )}
                        </td>
                      ))}

                      {/* Actions */}
                      <td className="text-center">
                        {editRoleId === role.rid ? (
                          <div className="action-buttons">
                            <button
                              className="btn-icon btn-save"
                              onClick={saveRole}
                              disabled={loading}
                              title="Save"
                            >
                              <i className="bi bi-check-lg"></i>
                            </button>
                            <button
                              className="btn-icon btn-cancel"
                              onClick={cancelEditing}
                              disabled={loading}
                              title="Cancel"
                            >
                              <i className="bi bi-x-lg"></i>
                            </button>
                          </div>
                        ) : (
                          <div className="action-buttons">
                            <button
                              className="btn-icon btn-edit"
                              onClick={() => startEditing(role)}
                              disabled={loading}
                              title="Edit Role"
                            >
                              <i className="bi bi-pencil-square"></i>
                            </button>
                            <button
                              className="btn-icon btn-delete"
                              onClick={() => deleteRole(role.rid)}
                              disabled={loading}
                              title="Delete Role"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .user-roles-container {
          padding: 0;
        }

        .roles-card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 15px rgba(0, 136, 204, 0.08);
          border: 1px solid #e0e6ed;
          overflow: hidden;
        }

        /* Animations */
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .slide-in { animation: slideIn 0.3s ease; }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        /* Header - Exact Match with UserCreation */
        .roles-header {
          background: var(--theme-color, #0088cc);
          padding: clamp(10px, 1vw, 14px) clamp(12px, 1.2vw, 16px);
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 2px solid #00d4aa;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .header-icon {
          width: clamp(32px, 3vw, 40px);
          height: clamp(32px, 3vw, 40px);
          background: rgba(255, 255, 255, 0.2);
          border-radius: clamp(6px, 0.6vw, 8px);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: clamp(16px, 1.6vw, 20px);
          color: white;
          animation: pulse 2s ease-in-out infinite;
        }

        .header-text h5 {
          margin: 0;
          color: white;
          font-size: clamp(12px, 1.1vw, 14px);
          font-weight: 700;
          line-height: 1.2;
        }

        .header-text p {
          margin: 0;
          color: rgba(255, 255, 255, 0.85);
          font-size: clamp(9px, 0.8vw, 10px);
        }

        .header-badge {
          background: rgba(255, 255, 255, 0.95);
          padding: 0.35rem 0.85rem;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .badge-count {
          font-size: clamp(12px, 1.1vw, 16px);
          font-weight: 700;
          color: var(--theme-color, #0088cc);
        }

        .badge-label {
          font-size: clamp(9px, 0.8vw, 11px);
          color: #666;
          font-weight: 600;
        }

        /* Alerts */
        .alert-custom {
          margin: clamp(10px, 1vw, 14px) clamp(12px, 1.2vw, 16px) 0;
          padding: clamp(8px, 0.8vw, 10px) clamp(10px, 1vw, 12px);
          border-radius: clamp(5px, 0.5vw, 6px);
          display: flex;
          align-items: center;
          gap: clamp(6px, 0.6vw, 8px);
          font-size: clamp(10px, 0.9vw, 11px);
          font-weight: 500;
          position: relative;
        }
        .alert-danger { background: #fff5f5; color: #e74c3c; border: 1px solid #fab1a0; }
        .alert-success { background: #f0fff4; color: #27ae60; border: 1px solid #a3e635; }
        .alert-custom i { font-size: 18px; }
        .alert-close {
          position: absolute; right: 12px; background: transparent; border: none;
          cursor: pointer; padding: 4px; color: inherit; opacity: 0.6; transition: opacity 0.2s;
        }
        .alert-close:hover { opacity: 1; }

        /* Table Wrapper */
        .table-wrapper {
          padding: clamp(10px, 1vw, 16px);
        }

        .table-responsive {
          overflow-x: auto;
          border: 1px solid #e0e6ed;
          border-radius: 8px;
        }

        /* Custom Table */
        .custom-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          font-size: clamp(11px, 0.9vw, 13px);
        }

        .custom-table thead tr {
          background: var(--theme-color, #0088cc);
        }

        .custom-table thead th {
          padding: clamp(8px, 0.8vw, 12px) clamp(10px, 1vw, 14px);
          text-align: left;
          font-weight: 600;
          color: white;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          white-space: nowrap;
          border: none;
        }

        .custom-table tbody tr {
          background: white;
          transition: all 0.2s;
        }

        .custom-table tbody tr:hover {
          background: #f8f9fa;
        }
        
        .custom-table tbody tr.editing-row {
          background: #f0f7ff;
        }

        .custom-table tbody td {
          padding: clamp(8px, 0.8vw, 12px) clamp(10px, 1vw, 14px);
          border-bottom: 1px solid #e0e6ed;
          vertical-align: middle;
          color: #2c3e50;
        }

        .custom-table tbody tr:last-child td {
          border-bottom: none;
        }

        .text-center { text-align: center !important; }
        .fw-semibold { font-weight: 600; }

        /* Form Controls inside Table */
        .form-control-custom, .form-select-custom {
          width: 100%;
          padding: 6px 8px;
          border: 2px solid #cbd5e0;
          border-radius: 6px;
          font-size: clamp(10px, 0.85vw, 12px);
          color: #2c3e50;
          transition: all 0.3s ease;
          outline: none;
          background: white;
        }

        .form-control-custom:focus, .form-select-custom:focus {
          border-color: var(--theme-color, #0088cc);
          box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
        }
        
        .form-control-custom.readonly {
          background: #f3f4f6;
          cursor: not-allowed;
          color: #7f8c8d;
        }

        /* Badges */
        .module-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: #f0f4f8;
          color: var(--theme-color, #0088cc);
          padding: 4px 10px;
          border-radius: 6px;
          font-weight: 600;
          font-size: clamp(10px, 0.85vw, 12px);
          border: 1px solid rgba(0, 136, 204, 0.1);
        }

        .permission-badge {
          display: inline-block;
          padding: 3px 10px;
          border-radius: 20px;
          font-size: clamp(10px, 0.85vw, 11px);
          font-weight: 700;
        }
        
        .permission-badge.granted {
          background: #dcfce7;
          color: #166534;
        }
        
        .permission-badge.denied {
          background: #fee2e2;
          color: #dc2626;
        }

        .pass-text {
          color: #6b7280;
          font-family: monospace;
          font-size: clamp(11px, 0.9vw, 13px);
          background: #f3f4f6;
          padding: 3px 8px;
          border-radius: 4px;
        }

        /* Action Buttons */
        .action-buttons {
          display: flex;
          gap: 6px;
          justify-content: center;
        }

        .btn-icon {
          width: 28px;
          height: 28px;
          border: none;
          border-radius: 6px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 13px;
        }

        .btn-edit { background: #e0f2fe; color: #0369a1; }
        .btn-edit:hover { background: #0369a1; color: white; transform: translateY(-2px); box-shadow: 0 3px 8px rgba(3, 105, 161, 0.2); }

        .btn-delete { background: #fee2e2; color: #dc2626; }
        .btn-delete:hover { background: #dc2626; color: white; transform: translateY(-2px); box-shadow: 0 3px 8px rgba(220, 38, 38, 0.2); }

        .btn-save { background: #d1fae5; color: #065f46; }
        .btn-save:hover { background: #059669; color: white; transform: translateY(-2px); box-shadow: 0 3px 8px rgba(5, 150, 105, 0.2); }

        .btn-cancel { background: #f3f4f6; color: #4b5563; }
        .btn-cancel:hover { background: #6b7280; color: white; transform: translateY(-2px); box-shadow: 0 3px 8px rgba(107, 114, 128, 0.2); }

        .btn-icon:disabled { opacity: 0.5; cursor: not-allowed; transform: none; box-shadow: none; }

        /* Loading & Empty State */
        .loading-state {
          text-align: center;
          padding: 40px 20px;
        }

        .spinner-custom {
          width: 40px;
          height: 40px;
          border: 4px solid #e0e6ed;
          border-top-color: var(--theme-color, #0088cc);
          border-radius: 50%;
          margin: 0 auto 15px;
          animation: spin 1s linear infinite;
        }

        .loading-state p { color: #7f8c8d; font-size: 13px; margin: 0; }

        .empty-state {
          text-align: center;
          padding: 3rem;
        }
        .empty-state i {
          font-size: 3rem;
          color: #d1d5db;
          margin-bottom: 1rem;
          display: block;
        }
        .empty-state h6 {
          color: #374151;
          font-weight: 600;
          font-size: 1.1rem;
          margin-bottom: 0.5rem;
        }
        .empty-state p { color: #6b7280; font-size: 0.9rem; margin: 0; }

        /* Responsive */
        @media (max-width: 768px) {
          .roles-header {
            flex-direction: column;
            gap: 10px;
            align-items: flex-start;
          }
          .header-badge { width: 100%; justify-content: center; }
          .custom-table { min-width: 800px; }
        }
      `}</style>
    </div>
  );
};

export default UserRolesTable;